import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Loader2, Plus, X, SlidersHorizontal, CreditCard } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import TenantModulesDialog from './TenantModulesDialog';
import PaymentLinkDialog from './PaymentLinkDialog';
import SignupOffers from './SignupOffers';
import ReferralProgram from './ReferralProgram';
import { DiscountCell } from './DiscountCell';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const field = 'w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm ' +
  'focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500';

const STATES = ['trialing', 'active', 'trial_expired', 'past_due', 'cancelled', 'suspended'];
const STATE_TONE = {
  active: 'bg-teal-100 text-teal-700', trialing: 'bg-sky-100 text-sky-700',
  trial_expired: 'bg-red-100 text-red-700', past_due: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-slate-200 text-slate-600', suspended: 'bg-red-100 text-red-700',
};

/** Driveline's own console: every workshop on the platform, what they pay, and
 *  the levers to onboard one manually or put one on hold. */
const PlatformConsole = () => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [coreModules, setCoreModules] = useState([]);
  const [moduleTenant, setModuleTenant] = useState(null);
  const [linkTenant, setLinkTenant] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ workshop_name: '', owner_name: '', email: '', phone: '', password: '', plan: 'professional' });

  const load = useCallback(async () => {
    const [t, m] = await Promise.all([
      axios.get(`${API}/platform/tenants`, { headers: authHeader() }),
      axios.get(`${API}/platform/metrics`, { headers: authHeader() }),
    ]);
    setRows(t.data.tenants || []);
    setPlans(t.data.plans || []);
    setModules(t.data.modules || []);
    setCoreModules(t.data.core_modules || []);
    setMetrics(m.data);
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, changes) => {
    await axios.patch(`${API}/platform/tenants/${id}`, changes, { headers: authHeader() });
    await load();
  };

  const create = async (e) => {
    e.preventDefault();
    setErr(''); setSaving(true);
    try {
      await axios.post(`${API}/platform/tenants`, form, { headers: authHeader() });
      setShowNew(false);
      setForm({ workshop_name: '', owner_name: '', email: '', phone: '', password: '', plan: 'professional' });
      await load();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (d?.[0]?.msg || 'Could not create the account.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Loading accounts…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="platform-console">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Building2 size={22} className="text-slate-400" /> Workshop accounts
          </h1>
          <p className="text-sm text-slate-500 mt-1">Every workshop subscribed to Driveline.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-4 py-2.5 rounded-xl"
          data-testid="platform-new-tenant-btn"
        >
          <Plus size={16} /> Add workshop
        </button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="platform-metrics">
          {[
            ['Accounts', metrics.total_accounts],
            ['Paying', metrics.by_state?.active || 0],
            ['On trial', metrics.by_state?.trialing || 0],
            ['Monthly revenue', money(metrics.mrr_inr)],
          ].map(([label, value]) => (
            <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
              <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[52rem]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Workshop</th>
              <th className="text-left px-4 py-2.5">Owner</th>
              <th className="text-left px-4 py-2.5">Plan</th>
              <th className="text-left px-4 py-2.5">Discount</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Staff</th>
              <th className="text-right px-4 py-2.5">Invoices</th>
              <th className="text-right px-4 py-2.5">Modules</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`platform-tenant-${t.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{t.workshop_name}</div>
                  <div className="text-xs text-slate-400">{t.slug}{t.is_founding_account ? ' · founding' : ''}</div>
                  {!t.is_founding_account && (
                    <button onClick={() => setLinkTenant(t)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900" data-testid={`platform-paylink-${t.slug}`}>
                      <CreditCard size={12} /> Payment link
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div>{t.owner_name}</div>
                  <div className="text-xs text-slate-400">{t.owner_email}</div>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={t.plan}
                    onChange={(e) => patch(t.id, { plan: e.target.value })}
                    className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white"
                    data-testid={`platform-plan-${t.slug}`}
                  >
                    {plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <DiscountCell tenant={t} plans={plans} onSave={patch} />
                </td>
                <td className="px-4 py-3">
                  <select
                    value={t.access?.state || t.subscription_status}
                    onChange={(e) => patch(t.id, { subscription_status: e.target.value })}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold border-0 ${STATE_TONE[t.access?.state] || 'bg-slate-100'}`}
                    data-testid={`platform-status-${t.slug}`}
                  >
                    {STATES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                  {t.access?.state === 'trialing' && (
                    <div className="text-[11px] text-slate-400 mt-1">{t.access.days_left}d left</div>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{t.staff_count}</td>
                <td className="px-4 py-3 text-right tabular-nums">{t.invoice_count}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setModuleTenant(t)}
                    className="inline-flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-900"
                    data-testid={`platform-modules-${t.slug}`}
                  >
                    <SlidersHorizontal size={14} />
                    {(t.access?.features || []).length}
                    {(t.module_addons || []).length > 0 && <span className="text-sky-600">+{t.module_addons.length}</span>}
                    {(t.view_only_features || []).length > 0 && <span className="text-slate-400">({t.view_only_features.length} view-only)</span>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SignupOffers />
      <ReferralProgram />

      {linkTenant && <PaymentLinkDialog tenant={linkTenant} onClose={() => setLinkTenant(null)} />}

      {moduleTenant && (
        <TenantModulesDialog
          tenant={moduleTenant}
          modules={modules}
          coreModules={coreModules}
          onClose={() => setModuleTenant(null)}
          onSaved={load}
        />
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <form
            onSubmit={create}
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 space-y-3 max-h-[92vh] overflow-y-auto"
            data-testid="platform-new-tenant-form"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add a workshop</h2>
              <button type="button" onClick={() => setShowNew(false)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="platform-new-close">
                <X size={18} />
              </button>
            </div>
            <input className={field} placeholder="Workshop name" required value={form.workshop_name}
                   onChange={(e) => setForm({ ...form, workshop_name: e.target.value })} data-testid="platform-new-workshop" />
            <input className={field} placeholder="Owner name" required value={form.owner_name}
                   onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="platform-new-owner" />
            <input className={field} type="email" placeholder="Owner email (their login)" required value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="platform-new-email" />
            <input className={field} placeholder="Mobile" value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="platform-new-phone" />
            <input className={field} type="text" placeholder="Temporary password (min 8)" required value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="platform-new-password" />
            <select className={field} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="platform-new-plan">
              {plans.map((p) => <option key={p.key} value={p.key}>{p.name} — {money(p.price_monthly)}/mo</option>)}
            </select>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="platform-new-error">{err}</div>}
            <button type="submit" disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold py-3 rounded-xl"
                    data-testid="platform-new-submit">
              {saving && <Loader2 className="animate-spin" size={16} />}
              {saving ? 'Creating…' : 'Create account'}
            </button>
            <p className="text-xs text-slate-400">
              They start on a 14-day trial. Share the email and temporary password with the owner.
            </p>
          </form>
        </div>
      )}
    </div>
  );
};

export default PlatformConsole;
